import {
  ApolloClient,
  ApolloLink,
  concat,
  createHttpLink,
  InMemoryCache,
  Observable,
  split,
} from "@apollo/client";
import { getMainDefinition } from "@apollo/client/utilities";
import { WebSocketLink } from "@apollo/client/link/ws";
import ConfigurableValues from "../config/constants";

/**
 * Sets up Apollo GraphQL client with HTTP and WebSocket links
 * Handles authentication via JWT tokens and supports subscriptions
 * @returns {ApolloClient} Configured Apollo client instance
 */
const setupApollo = () => {
  const { SERVER_URL, WS_SERVER_URL } = ConfigurableValues();
  // Initialize in-memory cache for GraphQL queries
  const cache = new InMemoryCache();
  // HTTP link for regular GraphQL queries and mutations
  const httpLink = createHttpLink({
    uri: `${SERVER_URL}graphql`,
  });

  // WebSocket link for GraphQL subscriptions with auto-reconnect
  const wsLink = new WebSocketLink({
    uri: `${WS_SERVER_URL}graphql`,
    options: {
      reconnect: true,
    },
  });

  // Add JWT token to request headers for authentication
  const request = async (operation) => {
    const data = localStorage.getItem("token");

    let token = null;
    if (data) {
      token = data;
    }
    // Set authorization header with Bearer token
    operation.setContext({
      headers: {
        authorization: token ? `Bearer ${token}` : "",
      },
    });
  };

  // Custom link to handle authentication for each request
  const requestLink = new ApolloLink(
    (operation, forward) =>
      new Observable((observer) => {
        let handle;
        Promise.resolve(operation)
          .then((oper) => request(oper))
          .then(() => {
            handle = forward(operation).subscribe({
              next: observer.next.bind(observer),
              error: observer.error.bind(observer),
              complete: observer.complete.bind(observer),
            });
          })
          .catch(observer.error.bind(observer));

        return () => {
          if (handle) handle.unsubscribe();
        };
      })
  );

  // Split link: use WebSocket for subscriptions, HTTP for queries/mutations
  const terminatingLink = split(({ query }) => {
    const { kind, operation } = getMainDefinition(query);
    return kind === "OperationDefinition" && operation === "subscription";
  }, wsLink);

  // Create Apollo client with combined links and cache
  const client = new ApolloClient({
    link: concat(ApolloLink.from([terminatingLink, requestLink]), httpLink),
    cache,
    resolvers: {},
    connectToDevTools: true, // Enable Apollo DevTools in development
  });

  return client;
};

export default setupApollo;
